'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

type AlertType = 'all' | 'injury' | 'goalie_confirmed' | 'projection_change' | 'high_confidence' | 'b2b' | 'statistical_anomaly';
type Severity = 'all' | 'critical' | 'high' | 'medium' | 'low';

interface Alert {
  id: string;
  type: string;
  severity: string;
  team: string | null;
  player: string | null;
  title: string;
  message: string;
  time: string;
  isRead: boolean;
}

const ALERTS: Alert[] = [
  { id: '1', type: 'injury', severity: 'high', team: 'TOR', player: 'A. Matthews', title: 'Blessure Matthews — Questionable', message: 'Auston Matthews questionable ce soir — genou droit. Impact majeur sur projections TOR. Win prob TOR ajustée de 62% à 54%.', time: '21:45', isRead: false },
  { id: '2', type: 'high_confidence', severity: 'high', team: 'COL', player: null, title: 'Signal haute confiance — COL', message: 'Colorado Avalanche: 78.9% probabilité victoire vs DAL. Confiance modèle: 0.89. Corsi: 56.8%. xGoals: 3.78.', time: '21:30', isRead: false },
  { id: '3', type: 'goalie_confirmed', severity: 'low', team: 'EDM', player: 'S. Skinner', title: 'Gardien confirmé — EDM', message: 'Stuart Skinner confirmé partant vs VGK. SV% L10: .921. GAA L10: 2.43. Performance domicile: .934.', time: '21:15', isRead: false },
  { id: '4', type: 'projection_change', severity: 'medium', team: 'FLA', player: null, title: 'Changement projection — FLA', message: 'Probabilité FLA ajustée de 52% à 47% suite aux nouvelles blessures. Modèle recalibré avec données temps réel.', time: '20:58', isRead: true },
  { id: '5', type: 'b2b', severity: 'medium', team: 'NYR', player: null, title: 'Back-to-back — NYR', message: 'Rangers jouaient hier vs PHI (victoire 4-2). Fatigue calendrier score: 3.2/5. Impact estimé: -3.4% win prob.', time: '20:30', isRead: true },
  { id: '6', type: 'statistical_anomaly', severity: 'medium', team: 'CAR', player: null, title: 'Anomalie statistique — CAR', message: 'Carolina: xGoals 40% au-dessus de la moyenne sur les 5 derniers matchs. Corsi: 58.1% (top 3 ligue).', time: '19:45', isRead: true },
  { id: '7', type: 'injury', severity: 'critical', team: 'NYR', player: 'V. Trocheck', title: 'Blessure critique — NYR', message: 'Vincent Trocheck OUT — upper body. Absence estimée: 2-3 semaines. Impact majeur sur PP NYR (-4.2%).', time: '18:30', isRead: true },
  { id: '8', type: 'goalie_confirmed', severity: 'low', team: 'VGK', player: 'A. Hill', title: 'Backup confirmé — VGK', message: 'Adin Hill remplace Thompson blessé. SV% saison: .908. Attention: 3ème match en 4 jours.', time: '17:15', isRead: true },
];

const ALERT_CONFIG: Record<string, { icon: string; color: string; bg: string; border: string }> = {
  injury: { icon: 'UserMinusIcon', color: 'text-negative', bg: 'bg-negative-subtle', border: 'border-negative/20' },
  goalie_confirmed: { icon: 'ShieldCheckIcon', color: 'text-primary', bg: 'bg-info-subtle', border: 'border-primary/20' },
  projection_change: { icon: 'ArrowsRightLeftIcon', color: 'text-accent', bg: 'bg-accent/10', border: 'border-accent/20' },
  high_confidence: { icon: 'FireIcon', color: 'text-positive', bg: 'bg-positive-subtle', border: 'border-positive/20' },
  b2b: { icon: 'ClockIcon', color: 'text-warning', bg: 'bg-warning-subtle', border: 'border-warning/20' },
  statistical_anomaly: { icon: 'ExclamationTriangleIcon', color: 'text-warning', bg: 'bg-warning-subtle', border: 'border-warning/20' },
};

const SEVERITY_COLOR: Record<string, string> = {
  critical: 'bg-negative',
  high: 'bg-negative',
  medium: 'bg-warning',
  low: 'bg-positive',
};

const CHANNELS = [
  { id: 'browser', label: 'Navigateur', icon: 'ComputerDesktopIcon', enabled: true },
  { id: 'email', label: 'Email', icon: 'EnvelopeIcon', enabled: true },
  { id: 'discord', label: 'Discord Webhook', icon: 'ChatBubbleLeftRightIcon', enabled: false },
  { id: 'telegram', label: 'Telegram', icon: 'PaperAirplaneIcon', enabled: false },
];

export default function AlertsClient() {
  const [typeFilter, setTypeFilter] = useState<AlertType>('all');
  const [severityFilter, setSeverityFilter] = useState<Severity>('all');
  const [channels, setChannels] = useState(CHANNELS);
  const [alerts, setAlerts] = useState(ALERTS);
  const [discordWebhook, setDiscordWebhook] = useState('');
  const [telegramChatId, setTelegramChatId] = useState('');
  const [showSettings, setShowSettings] = useState(false);

  const filtered = alerts.filter(a => {
    const typeMatch = typeFilter === 'all' || a.type === typeFilter;
    const sevMatch = severityFilter === 'all' || a.severity === severityFilter;
    return typeMatch && sevMatch;
  });

  const unreadCount = alerts.filter(a => !a.isRead).length;

  const markAllRead = () => setAlerts(prev => prev.map(a => ({ ...a, isRead: true })));
  const toggleChannel = (id: string) => setChannels(prev => prev.map(c => c.id === id ? { ...c, enabled: !c.enabled } : c));

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Smart Alert System</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Alertes intelligentes — Blessures, gardiens, anomalies statistiques
          </p>
        </div>
        <div className="flex items-center gap-3">
          {unreadCount > 0 && (
            <span className="status-badge bg-negative-subtle text-negative border border-negative/20 font-mono-data">
              {unreadCount} non lues
            </span>
          )}
          <button onClick={markAllRead} className="btn-ghost text-xs flex items-center gap-1.5">
            <Icon name="CheckIcon" size={13} />
            Tout marquer lu
          </button>
          <button onClick={() => setShowSettings(!showSettings)} className="btn-secondary flex items-center gap-2 text-xs">
            <Icon name="BellIcon" size={13} />
            Notifications
          </button>
        </div>
      </div>

      {/* Notification channels settings */}
      {showSettings && (
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="BellAlertIcon" size={14} className="text-primary" />
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Canaux de notification</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {channels.map((ch) => (
              <div key={ch.id} className={`rounded-lg p-3 border transition-all ${ch.enabled ? 'border-primary/30 bg-primary/5' : 'border-border bg-muted/30'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon name={ch.icon as Parameters<typeof Icon>[0]['name']} size={14} className={ch.enabled ? 'text-primary' : 'text-muted-foreground'} />
                    <span className="text-xs font-medium text-foreground">{ch.label}</span>
                  </div>
                  <button
                    onClick={() => toggleChannel(ch.id)}
                    className={`w-8 h-4 rounded-full transition-all relative ${ch.enabled ? 'bg-primary' : 'bg-muted'}`}
                  >
                    <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${ch.enabled ? 'left-4.5 translate-x-0.5' : 'left-0.5'}`} />
                  </button>
                </div>
                {ch.id === 'discord' && ch.enabled && (
                  <input
                    type="text"
                    placeholder="Webhook URL..."
                    value={discordWebhook}
                    onChange={e => setDiscordWebhook(e.target.value)}
                    className="w-full bg-input border border-border rounded px-2 py-1 text-2xs text-foreground mt-1"
                  />
                )}
                {ch.id === 'telegram' && ch.enabled && (
                  <input
                    type="text"
                    placeholder="Chat ID..."
                    value={telegramChatId}
                    onChange={e => setTelegramChatId(e.target.value)}
                    className="w-full bg-input border border-border rounded px-2 py-1 text-2xs text-foreground mt-1"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-2xs text-muted-foreground uppercase tracking-wider">Type:</span>
        {(['all', 'injury', 'goalie_confirmed', 'high_confidence', 'projection_change', 'b2b', 'statistical_anomaly'] as AlertType[]).map(t => (
          <button
            key={t}
            onClick={() => setTypeFilter(t)}
            className={`status-badge text-2xs cursor-pointer transition-all ${typeFilter === t ? 'bg-primary/20 text-primary border border-primary/30' : 'bg-muted text-muted-foreground border border-border hover:border-primary/20'}`}
          >
            {t === 'all' ? 'Tous' : t === 'goalie_confirmed' ? 'Gardien' : t === 'high_confidence' ? 'Haute conf.' : t === 'projection_change' ? 'Projection' : t === 'statistical_anomaly' ? 'Anomalie' : t === 'b2b' ? 'B2B' : t}
          </button>
        ))}
        <span className="text-2xs text-muted-foreground uppercase tracking-wider ml-2">Sévérité:</span>
        {(['all', 'critical', 'high', 'medium', 'low'] as Severity[]).map(s => (
          <button
            key={s}
            onClick={() => setSeverityFilter(s)}
            className={`status-badge text-2xs cursor-pointer transition-all ${severityFilter === s ? 'bg-primary/20 text-primary border border-primary/30' : 'bg-muted text-muted-foreground border border-border hover:border-primary/20'}`}
          >
            {s === 'all' ? 'Tous' : s}
          </button>
        ))}
      </div>

      {/* Alert list */}
      <div className="space-y-3">
        {filtered.map((alert) => {
          const config = ALERT_CONFIG[alert.type] || ALERT_CONFIG.injury;
          return (
            <div
              key={alert.id}
              className={`card p-4 border transition-all ${!alert.isRead ? 'border-primary/20' : ''} ${config.border}`}
            >
              <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${config.bg}`}>
                  <Icon name={config.icon as Parameters<typeof Icon>[0]['name']} size={15} className={config.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${SEVERITY_COLOR[alert.severity]}`} />
                    <span className="text-sm font-semibold text-foreground">{alert.title}</span>
                    {!alert.isRead && (
                      <span className="status-badge bg-primary/10 text-primary border border-primary/20 text-2xs">Nouveau</span>
                    )}
                    <span className="ml-auto text-2xs font-mono-data text-muted-foreground">{alert.time} ET</span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{alert.message}</p>
                  {(alert.team || alert.player) && (
                    <div className="flex items-center gap-2 mt-2">
                      {alert.team && (
                        <span className="status-badge bg-muted text-foreground border border-border text-2xs font-mono-data">{alert.team}</span>
                      )}
                      {alert.player && (
                        <span className="text-2xs text-muted-foreground">{alert.player}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="card p-8 text-center">
            <Icon name="BellSlashIcon" size={24} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Aucune alerte pour ces filtres</p>
          </div>
        )}
      </div>

      {/* Alert stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Alertes aujourd\'hui', value: String(ALERTS.length), icon: 'BellAlertIcon', color: 'text-primary' },
          { label: 'Blessures actives', value: '2', icon: 'UserMinusIcon', color: 'text-negative' },
          { label: 'Haute confiance', value: '1', icon: 'FireIcon', color: 'text-positive' },
          { label: 'Gardiens confirmés', value: '2', icon: 'ShieldCheckIcon', color: 'text-accent' },
        ].map((stat) => (
          <div key={stat.label} className="card p-3">
            <div className="flex items-center gap-2 mb-1">
              <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={13} className={stat.color} />
              <span className="text-2xs text-muted-foreground">{stat.label}</span>
            </div>
            <p className={`text-xl font-bold font-mono-data ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
